import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../../api/client";
import { clearToken, setToken } from "../../api/token";
import { ME_QUERY_KEY } from "./useMe";

const storeSession = (queryClient, data) => {
  setToken(data.token, data.expires_at);
  // Seed the me-query so guards don't flash a loading state after login.
  queryClient.setQueryData(ME_QUERY_KEY, {
    user: data.user,
    role: data.role,
    profile: data.profile,
    needsEvaluation: data.needsEvaluation,
  });
};

export const useLogin = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ email, password }) =>
      apiFetch("/auth/login", { method: "POST", body: { email, password }, auth: false }),
    onSuccess: (data) => storeSession(queryClient, data),
  });
};

export const useSignupAthlete = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ email, password, fullName, sport, birthDate, country, guardianEmail, guardianName, consents }) =>
      apiFetch("/auth/signup", {
        method: "POST",
        auth: false,
        body: {
          role: "athlete",
          email,
          password,
          profile: { fullName, sport, birthDate, country, guardianEmail, guardianName, consents },
        },
      }),
    onSuccess: (data) => storeSession(queryClient, data),
  });
};

export const useSignupScout = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      email,
      password,
      fullName,
      phone,
      organizationName,
      // Resuelta (o creada) por el backend dentro de la transacción de signup:
      // /academies exige sesión y en el registro todavía no hay ninguna.
      academyName,
      details,
    }) =>
      apiFetch("/auth/signup", {
        method: "POST",
        auth: false,
        body: {
          role: "scout",
          email,
          password,
          profile: { fullName, phone, organizationName, academyName, details },
        },
      }),
    onSuccess: (data) => storeSession(queryClient, data),
  });
};

export const useLogout = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await apiFetch("/auth/logout", { method: "POST" }).catch(() => null);
    },
    onSettled: () => {
      clearToken();
      queryClient.clear();
    },
  });
};

export const useForgotPassword = () =>
  useMutation({
    mutationFn: ({ email }) =>
      apiFetch("/auth/forgot-password", { method: "POST", body: { email }, auth: false }),
  });

export const useResetPassword = () =>
  useMutation({
    mutationFn: ({ token, password }) =>
      apiFetch("/auth/reset-password", { method: "POST", body: { token, password }, auth: false }),
  });

// Cambio de contraseña desde la sesión abierta. El backend re-verifica la
// contraseña actual, así que quien tome una sesión prestada no puede cambiarla
// sin conocerla.
export const useChangePassword = () =>
  useMutation({
    mutationFn: ({ currentPassword, newPassword }) =>
      apiFetch("/auth/change-password", {
        method: "POST",
        body: { currentPassword, newPassword },
      }),
  });
